import { LayoutValuationResponse } from '../../../redux/studies.model';
import nls from '../../../nls/pt-BR.json';
import {
  labelFormatterMoneyAsInteger,
  unmaskAbsoluteValue,
} from '../../../../../../shared/formatters/formatters';
import {
  AddAlert,
  Button,
  IcoPencilEditStudy,
  IcoTrashCanOutline,
  IcoWarning,
} from '../../../../../../component';
import { z } from 'zod';
import { useState } from 'react';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { EditValuation } from './editValuation';
import { ExitGuideButton } from './exitGuideButton';
import { PreviousStepButton } from './previousStepButton';
import { patchStudyStep } from '../../../redux/studies.controller';
import { useTypedSelector } from '../../../../../../hooks/useTypedSelector';
import {
  advanceToStep,
  setLayoutsValuationPayload,
  setLayoutsValuationPrimaryResponse,
  setLayoutsValuationSecondaryResponse,
  studiesSelector,
} from '../../../redux/studies.reducer';
import { useDispatch } from 'react-redux';
import { setIsLoadingMap } from '../../../../redux/dashboardActions';

interface LayoutsValuationProps {
  valuationData: LayoutValuationResponse;
  isPrimaryMarket: boolean;
}

const layoutValuation = z.object({
  layoutId: z.string(),
  area: z.number(),
  pricePerSqMt: z.string(),
  pricePerUnit: z.string(),
  isEdited: z.boolean(),
});

const layoutValuationForm = z.object({ layouts: z.array(layoutValuation) });

export type EditLayoutValuation = z.infer<typeof layoutValuation>;
export type EditLayoutValuationFormData = z.infer<typeof layoutValuationForm>;

const getDefaultValues = (
  valuationData: LayoutValuationResponse
): EditLayoutValuation[] => {
  const formatted = valuationData?.map((el) => ({
    layoutId: el.id,
    area: el.area,
    pricePerSqMt: labelFormatterMoneyAsInteger(el.pricePerSqMt),
    pricePerUnit: labelFormatterMoneyAsInteger(el.pricePerUnit),
    isEdited: false,
  }));
  return formatted;
};

export const LayoutsValuation: React.FC<LayoutsValuationProps> = ({
  valuationData,
  isPrimaryMarket,
}) => {
  const [editMode, setEditMode] = useState<boolean[]>(
    valuationData.map((el) => false)
  );
  const { id, currentStep } = useTypedSelector(studiesSelector);
  const methods = useForm<EditLayoutValuationFormData>({
    resolver: zodResolver(layoutValuationForm),
    defaultValues: {
      layouts: getDefaultValues(valuationData),
    },
  });
  const dispatch = useDispatch();
  const { fields, remove } = useFieldArray<EditLayoutValuationFormData>({
    control: methods.control,
    name: 'layouts',
  });
  const handleClickEdit = (layoutIndex: number) => {
    const newEditMode = editMode.map((el, index) => index === layoutIndex);
    setEditMode(newEditMode);
  };
  const currentValues = methods.watch();
  const removeAt = (index: number) => {
    if (currentValues.layouts.length > 1) remove(index);
  };
  const isValidResponse = (data: EditLayoutValuation) => {
    const { pricePerUnit } = data;
    return unmaskAbsoluteValue(pricePerUnit.split('R$')[1]) > 0;
  };
  const onSubmit = async (data: EditLayoutValuationFormData) => {
    try {
      dispatch(setIsLoadingMap(true));
      const formattedLayouts = data.layouts.map((elem, index) => ({
        ...elem,
        pricePerSqMt: unmaskAbsoluteValue(elem.pricePerSqMt.split('R$')[1]),
        pricePerUnit: unmaskAbsoluteValue(elem.pricePerUnit.split('R$')[1]),
      }));
      const payload = { layouts: formattedLayouts };
      const stepNumber = isPrimaryMarket ? 2 : 3;
      const response = await patchStudyStep(stepNumber, id, payload);
      if (isPrimaryMarket) {
        dispatch(setLayoutsValuationPrimaryResponse(response.primary_market));
      } else {
        dispatch(
          setLayoutsValuationSecondaryResponse(response.secondary_market)
        );
      }
      dispatch([
        advanceToStep(currentStep.nextStep),
        setLayoutsValuationPayload(response.layoutCharacteristics),
      ]);
    } catch (e) {
      dispatch(AddAlert('warning', 'Erro ao atualizar avaliações'));
    } finally {
      dispatch(setIsLoadingMap(false));
    }
  };
  return (
    <>
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          {currentValues.layouts.map(
            (valuation: EditLayoutValuation, index) => {
              return (
                <div key={index} className="layouts-valuation__item-container">
                  <div
                    className="layouts-valuation__item-container__row"
                    style={{ alignItems: 'center' }}
                  >
                    <h6 className="layouts-valuation__item-container__title">
                      {nls.studyCreationGuide.layoutsValuation.layoutNumber}{' '}
                      {valuation.layoutId}
                    </h6>
                    {isPrimaryMarket && (
                      <Button
                        type="link"
                        cy="DeleteValuation"
                        style={{ padding: '0px 4px 0px 0px' }}
                        onClick={() => removeAt(index)}
                      >
                        <IcoTrashCanOutline
                          cy="DeleteValuation"
                          style={{ color: '#D31717' }}
                        />
                      </Button>
                    )}
                  </div>
                  <div className="layouts-valuation__item-container__row">
                    <p className="layouts-valuation__item-container__row__key">
                      {nls.studyCreationGuide.layoutsValuation.area}
                    </p>
                    <p>{valuation.area}</p>
                  </div>
                  {editMode[index] ? (
                    <EditValuation
                      valuationIndex={index}
                      setEditMode={setEditMode}
                      editMode={editMode}
                      valuation={valuation}
                    />
                  ) : (
                    <>
                      <div className="layouts-valuation__item-container__row">
                        <p className="layouts-valuation__item-container__row__key">
                          {nls.studyCreationGuide.layoutsValuation.pricePerSqMt}
                        </p>
                        <p>
                          {isValidResponse(valuation) ? (
                            currentValues.layouts[index].pricePerSqMt
                          ) : (
                            <IcoWarning cy="NoResponse" />
                          )}
                        </p>
                      </div>
                      <div className="layouts-valuation__item-container__row">
                        <p className="layouts-valuation__item-container__row__key">
                          {nls.studyCreationGuide.layoutsValuation.pricePerUnit}
                        </p>
                        <div
                          style={{
                            display: 'flex',
                            gap: 8,
                            alignItems: 'center',
                          }}
                        >
                          <Button
                            cy={`EditValuation[${valuation.layoutId}]`}
                            type="link"
                            onClick={() => handleClickEdit(index)}
                          >
                            <IcoPencilEditStudy
                              cy={`EditValuation[${valuation.layoutId}]`}
                            />
                          </Button>
                          <p>
                            {isValidResponse(valuation) ? (
                              currentValues.layouts[index].pricePerUnit
                            ) : (
                              <IcoWarning cy="NoResponse" />
                            )}
                          </p>
                        </div>
                      </div>
                      {!isValidResponse(valuation) && <InvalidResponse />}
                    </>
                  )}
                </div>
              );
            }
          )}
          <div className="study-creation-guide__buttons-container">
            <div>
              <ExitGuideButton />
            </div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: 8,
              }}
            >
              <PreviousStepButton />
              <button
                className="btn normal primary"
                data-cy="StudyCreationGuideNextStep"
                type="submit"
              >
                {nls.studyCreationGuide.buttons.forward}
              </button>
            </div>
          </div>
        </form>
      </FormProvider>
    </>
  );
};

const InvalidResponse: React.FC = () => {
  return (
    <div className="invalid-response">
      <IcoWarning cy="InvalidResponse" style={{ minWidth: 19 }} />
      <p>{nls.invalidResponse.helper}</p>
    </div>
  );
};
